import isMatch from 'lodash/isMatch'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { UseFormReturn } from '../../../../../../hooks'
import { ContactPerson } from '../../../../types/contactPerson'
import { CustomerFormSchema } from '../../types/customerForm/customerFormSchema'
import {
  isContactPersonEmailValid,
  isContactPersonMainFieldsFilled,
} from '../../utils/contactPersons/contactPersonValidation'
import { useContactPersonFormActions } from '../useContactPersonFormActions'
import { addNewPrimaryContactToContactPersons } from './utils/addNewPrimaryContactToContactPersons'

interface UseAddContactPersonProps {
  customerForm: UseFormReturn<CustomerFormSchema>
}

export const useAddContactPerson = ({ customerForm }: UseAddContactPersonProps) => {
  const { t } = useTranslation()
  const { getValues, setError, setValue } = customerForm
  const { resetContactFormFields, setMainPrimaryContactFields } = useContactPersonFormActions(customerForm)

  const addPrimaryContact = useCallback(
    (newPrimaryContact: ContactPerson) => {
      const { contactPersons = [], email, firstName, lastName } = getValues()

      const updatedContacts = addNewPrimaryContactToContactPersons({
        contactPersons,
        currentPrimaryContact: { email, firstName, lastName },
        newPrimaryContact,
      })

      setValue('contactPersons', updatedContacts, { shouldDirty: true })

      // This must be triggered for both customer types so adding a new primary contact in company view
      // updates the 3 primary contact inputs in individual view
      setMainPrimaryContactFields({
        email: newPrimaryContact.email,
        firstName: newPrimaryContact.firstName,
        lastName: newPrimaryContact.lastName,
      })
    },
    [getValues, setValue, setMainPrimaryContactFields],
  )

  const addRegularContact = useCallback(
    (newContact: ContactPerson) => {
      const { contactPersons = [] } = getValues()
      const contactAlreadyExists = contactPersons.some((contact) => isMatch(contact, newContact))

      if (!contactAlreadyExists) {
        setValue('contactPersons', [...contactPersons, newContact], {
          shouldDirty: true,
        })
      }
    },
    [getValues, setValue],
  )

  const addContactPerson = useCallback(() => {
    const { contactEmail, contactFirstName, contactLastName, isContactPrimary } = getValues()

    if (!isContactPersonMainFieldsFilled(contactEmail, contactFirstName, contactLastName)) {
      return false
    }

    if (!isContactPersonEmailValid(contactEmail)) {
      setError('contactEmail', {
        message: t('customer_modal.form.validation.invalid_email'),
      })

      return false
    }

    const newContactPerson: ContactPerson = {
      email: contactEmail,
      firstName: contactFirstName,
      isPrimary: !!isContactPrimary,
      lastName: contactLastName,
    }

    if (isContactPrimary) {
      addPrimaryContact(newContactPerson)
    } else {
      addRegularContact(newContactPerson)
    }

    resetContactFormFields()
    return true
  }, [addPrimaryContact, addRegularContact, getValues, resetContactFormFields, setError, t])

  return { addContactPerson }
}
